import React, { useEffect, useMemo, useCallback, useState } from 'react';
import Select from 'react-select';
import DatePickerPopup from "./DatePickerPopup.js";
import QueueFilter from './QueueFilter'; 

function AgentsFilter({ filters, setFilters, allAgentInfo, queues, initiationMethods }) {

  const dateOptions = useMemo(
    () => [
      { value: 'today', label: 'Today' },
      { value: 'thisWeek', label: 'This Week' },
      { value: 'thisMonth', label: 'This Month' },
      { value: 'thisYear', label: 'This Year' },
      { value: 'all', label: 'All' },
      { value: 'custom', label: 'Custom' },
    ],
    []
  );

  const [localDateStart, setLocalDateStart] = useState(new Date());
  const [localDateEnd, setLocalDateEnd] = useState(new Date());

  const [showDatePickerPopup, setShowDatePickerPopup] = useState(false);
  
  const formatDate = (date) => {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  };


    // Handle date picker confirmation
    const handleDatePickerConfirm = (startDate, endDate) => {
      setFilters((prev) => ({
        ...prev,
        dateStart: startDate,
        dateEnd: endDate,
        dateRange: {
          value: "custom",
          label: `${formatDate(startDate)} : ${formatDate(endDate)}`,
        },
      }));
      setShowDatePickerPopup(false);
    };

  const handleChange = useCallback(
    (selectedOptions, { name }) => {
      if (name === "dateRange") {
        if (selectedOptions.value === "custom") {
          setShowDatePickerPopup(true);
        } else {
          setFilters((prev) => ({
            ...prev,
            dateRange: selectedOptions,
            dateStart: null,
            dateEnd: null,
          }));
        }
      } else {
        setFilters((prev = {}) => ({
          ...prev,
          [name]: selectedOptions,
        }));
      }
    },
    [setFilters]
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setFilters((prev = {
        category: [],
        intent: [],
        agent: null,
        callId: '',
        dateRange: { value: 'today', label: 'Today' },
      }) => prev);
    }, 300);
  
    return () => {
      clearTimeout(handler);
    };
  }, [setFilters]);

  const formatOptionLabel = ({ label, count }, { context }) => {
    if (context === "menu") {
      return (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>{label}</span>
          {count !== undefined && (
            <span style={{ color: "#aaa", paddingLeft: "5px" }}>({count})</span>
          )}
        </div>
      );
    } else {
      return label;
    }
  };

  
  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '38px',
      height: 'auto',
    }),
    option: (provided) => ({
      ...provided,
      display: 'flex',
      justifyContent: 'space-between',
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#e2e8f0',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      fontSize: '14px',
    }),
    valueContainer: (provided) => ({
        ...provided,
        flexWrap: "wrap",
        overflowY: "auto",
        padding: "2px 6px",
    }),
    placeholder: (provided) => ({
        ...provided,
        whiteSpace: "nowrap",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 1000, // Increase this value as needed
      width: "auto",
      minWidth: "275px",  // Same as control width
      maxWidth: "550px",  // 2x the control width
      left: "50%",
      transform: "translateX(-50%)",
    }),
  };
  const formattedAgentInfo = useMemo(() => {
    return allAgentInfo.map(agent => ({
      value: agent.agent,  // Assuming 'agent' is the identifier
      label: agent.agent,  // You can modify this if you want to display a different label
      count: agent.totalCalls
    }));
  }, [allAgentInfo]);

  const handleExcludeWeekends = (event) => {
    console.log("Handle exclude weekends", event.target.checked);
    setFilters((prev) => ({ ...prev, excludeWeekends: event.target.checked }));
  };

  return (
    <div className="mb-4 p-4 rounded-lg">
      <div className="flex space-x-8 mb-1 ml-5 mr-5">
        <div className="flex-1 relative">
          <Select
            name="dateRange"
            value={
              filters.dateRange &&
              filters.dateRange.value === "custom" &&
              filters.dateStart &&
              filters.dateEnd
                ? {
                    value: "custom",
                    label: `Custom - ${formatDate(filters.dateStart)} : ${formatDate(
                      filters.dateEnd
                    )}`,
                  }
                : filters.dateRange
            }
            onChange={(option) => handleChange(option, { name: "dateRange" })}
            options={dateOptions}
            styles={customStyles}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: "rgb(56 126 252)",
              },
            })}
          />
        </div>

          <>
            <div className="flex-1 relative">
                <QueueFilter
                    queues={queues}
                    selectedQueues={filters.queue || []}
                    onChange={(selectedQueues) =>
                        handleChange(selectedQueues, { name: "queue" })
                    }
                    placeholder="Select Queue"
                />
            </div>

            <div className="flex-1 relative">
              <Select
                name="initiationMethod"
                value={filters.initiationMethod}
                onChange={(option) =>
                  handleChange(option, { name: "initiationMethod" })
                }
                options={initiationMethods}
                isMulti
                isClearable
                formatOptionLabel={formatOptionLabel}
                placeholder="Select Contact Origination"
                styles={customStyles}
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary: "rgb(56 126 252)",
                  },
                })}
              />
            </div>


            <div className="flex-1 relative">
              <Select
                name="agent"
                value={filters.agent}
                onChange={(option) => handleChange(option, { name: "agent" })}
                options={formattedAgentInfo}
                isClearable
                placeholder="Select Agent"
                formatOptionLabel={formatOptionLabel}
                styles={customStyles}
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary: "rgb(56 126 252)",
                  },
                })}
              />
            </div>
          </>
      </div>

      {/* New checkbox row */}
      <div className={`flex justify-end mt-3 ml-8 mr-5 ${filters.dateRange.value === "today" ? "invisible" : ""}`}>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={filters.excludeWeekends}
              onChange={handleExcludeWeekends}
              className="form-checkbox h-5 w-5 usan-primary"
            />
            <span className="text-sm">Exclude weekends</span>
          </label>
        </div>

      {/* Date Picker Popup */}
      <DatePickerPopup
        isOpen={showDatePickerPopup}
        onClose={() => setShowDatePickerPopup(false)}
        onConfirm={handleDatePickerConfirm}
        startDate={localDateStart}
        endDate={localDateEnd}
        onStartDateChange={setLocalDateStart}
        onEndDateChange={setLocalDateEnd}
      />
    </div>
  );
}

export default AgentsFilter;
